import { createContext, useContext, useEffect, useRef, useState } from "react";
import { dorotheeVoice } from "./dorotheeVoice";

const SoundContext = createContext(null);

// Very discreet synthesized purr using the Web Audio API (no external asset).
function createPurrEngine() {
  let ctx = null, src = null, gain = null, lfo = null, lfoGain = null, filter = null, running = false;

  const ensure = () => {
    if (ctx) return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    ctx = new AC();
    // brown-ish noise buffer
    const buffer = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < data.length; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.2;
    }
    src = ctx.createBufferSource();
    src.buffer = buffer; src.loop = true;
    filter = ctx.createBiquadFilter();
    filter.type = "lowpass"; filter.frequency.value = 190;
    gain = ctx.createGain(); gain.gain.value = 0;
    lfo = ctx.createOscillator(); lfo.frequency.value = 26; // rumble rate
    lfoGain = ctx.createGain(); lfoGain.gain.value = 0.02;
    lfo.connect(lfoGain); lfoGain.connect(gain.gain);
    src.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
    src.start(); lfo.start();
  };

  return {
    start() {
      ensure();
      if (!ctx || running) return;
      if (ctx.state === "suspended") ctx.resume();
      gain.gain.setTargetAtTime(0.045, ctx.currentTime, 1.2);
      lfoGain.gain.setTargetAtTime(0.02, ctx.currentTime, 0.1);
      running = true;
    },
    stop() {
      if (!ctx || !running) return;
      gain.gain.setTargetAtTime(0, ctx.currentTime, 0.6);
      lfoGain.gain.setTargetAtTime(0, ctx.currentTime, 0.1);
      running = false;
    },
  };
}

export function SoundProvider({ children }) {
  const [muted, setMuted] = useState(() => localStorage.getItem("dl_muted") === "1");
  const [interacted, setInteracted] = useState(false);
  const engine = useRef(null);
  const voiceBusy = useRef(false);

  useEffect(() => {
    engine.current = createPurrEngine();
    const unsubscribe = dorotheeVoice.subscribe(state => {
      voiceBusy.current = state.busy;
      if (state.busy) engine.current?.stop();
    });
    const onFirst = () => setInteracted(true);
    window.addEventListener("pointerdown", onFirst, { once: true });
    window.addEventListener("keydown", onFirst, { once: true });
    return () => { unsubscribe(); engine.current?.stop(); window.removeEventListener("pointerdown", onFirst); window.removeEventListener("keydown", onFirst); };
  }, []);

  const toggleMuted = () => setMuted((m) => { const v = !m; localStorage.setItem("dl_muted", v ? "1" : "0"); if (v) { engine.current?.stop(); dorotheeVoice.stop(); } return v; });
  const startPurr = () => { if (!muted && interacted && !voiceBusy.current) engine.current?.start(); };
  const stopPurr = () => engine.current?.stop();

  return (
    <SoundContext.Provider value={{ muted, toggleMuted, startPurr, stopPurr, interacted }}>
      {children}
    </SoundContext.Provider>
  );
}

export const useSound = () => useContext(SoundContext);
